/**
 * Mixer fader math - pure transforms (no I/O), per the "math in
 * {topic}Edit.ts, store = thin wrappers" convention.
 *
 * DEC-M2 (MIXER-PAGE-DESIGN.md): the fader throw position maps 1:1 to Live's
 * normalized `mixer_device.volume` value 0..1 - faithful to Live's own fader,
 * where the handle position IS the normalized value. Unity (0 dB) therefore
 * sits at position 0.85. The value we SEND is the normalized 0..1; the dB is
 * a display-only readout computed here.
 *
 * The dB curve is deliberately APPROXIMATE (display-only; the operator times
 * to audio, not to a screen number). TODO (feedback half / device v0.2): the
 * device can emit Live's exact dB via LiveAPI `str_for_value` on
 * `mixer_device.volume` - swap the approximation for the exact string then.
 */

/** Normalized value at 0 dB (unity). Live's fader convention. */
export const UNITY = 0.85;

/** Half-width of the unity magnet window (in normalized units). A drag that
 *  lands within +-this of UNITY snaps to exactly UNITY, so the operator can
 *  find 0 dB by feel. */
export const UNITY_MAGNET = 0.012;

/** Below this normalized value the readout is -inf dB (fader effectively off). */
const SILENCE_FLOOR = 0.0002;

/** Fader position (0 = bottom, 1 = top) -> normalized device value. Identity
 *  today (1:1 with Live's fader); named so a future taper change has one home. */
export function positionToValue(frac: number): number {
  return clamp01(frac);
}

/** Normalized device value -> fader position (0 = bottom, 1 = top). */
export function valueToPosition(v: number): number {
  return clamp01(v);
}

/** Snap to exactly UNITY within the magnet window; otherwise pass through. */
export function applyUnityMagnet(v: number): number {
  return Math.abs(v - UNITY) <= UNITY_MAGNET ? UNITY : clamp01(v);
}

/**
 * Approximate dB for a normalized value. -Infinity below the silence floor.
 * - v >= UNITY: linear 0 -> +6 dB across the top of the throw.
 * - 0 < v < UNITY: 40*log10(v/UNITY) - 0 dB at unity, steepening toward -inf.
 */
export function normalizedToDb(v: number): number {
  const c = clamp01(v);
  if (c <= SILENCE_FLOOR) return -Infinity;
  if (c >= UNITY) return ((c - UNITY) / (1 - UNITY)) * 6;
  return 40 * Math.log10(c / UNITY);
}

/** Human dB label: "-inf", "0.0 dB", "+3.2 dB", "-12.4 dB". */
export function dbLabel(v: number): string {
  const db = normalizedToDb(v);
  if (db === -Infinity) return '-inf';
  const rounded = Math.round(db * 10) / 10;
  if (rounded === 0) return '0.0 dB';
  const sign = rounded > 0 ? '+' : '';
  return `${sign}${rounded.toFixed(1)} dB`;
}

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}
